import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useTenantId } from './useTenant';

/**
 * GET /api/tenants/<id>/fleet-integrity — every automated integrity/GPS
 * check this system runs, rolled up into one row per vehicle and one row
 * per driver, instead of a dispatcher only seeing each firing as a
 * separate notifications-bell line. See blueprints/reporting.py's
 * fleet_integrity() docstring for exactly which event_type prefixes feed
 * which row, why a diversion alert/shadow-driving pattern lands on the
 * DRIVER row rather than the vehicle row, and why gps_confirmed_delivery_rate
 * is null (not 0) for a vehicle with no current driver or no deliveries in
 * the window.
 *
 * Returns { vehicles, drivers, window_days }. Every active vehicle/driver
 * is present, including one with an all-zero `counts` — a clean truck is a
 * real answer here, not a row to omit. Both lists arrive pre-sorted
 * worst-first by `risk_score` (tiebroken by `total_worth_checking`).
 */
export function useFleetIntegrity() {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['fleet-integrity', tenantId],
    queryFn: () => api(`/api/tenants/${tenantId}/fleet-integrity`),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });
}
